import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { tz, todayISO, money, moneyTight, currencySymbol, localeDate, localeNumber } from '../../utils/region';
import CollectionCell from '../CollectionCell';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFocusRow } from '../MyTasks';
import { useBadgeOnly, BadgeFilterBar } from '../BadgeFilter';
import { api } from '../../utils/api';
import { PettyCashEntry } from '../../types';
import Pagination, { getPageSize } from '../Pagination';
import MatchPanel from '../MatchPanel';
import DocNoLink from '../DocNoLink';
import { docKindOf, useDocSeries } from '../../utils/docSeries';
import TaxPaidEmailComposer from '../TaxPaidEmailComposer';
import ReceiptEmailComposer from '../Peak/ReceiptEmailComposer';
import Fab from '../Fab';
import { tabColor, rowTint, menuPill, lighten, darken, TAB_GROUPS } from '../../tabPalette';
import { LevelLabel } from '../Jobs';
import { displayClientName, displayCompanyName, jobTitleLabel } from '../../utils/displayName';
import { brandOr } from '../../utils/brand';

// ── Tenant-zone time helpers (utils/region.ts) ───────────────────────────────────────────────
const todayLocal = (): string => todayISO(); // 'YYYY-MM-DD' in the tenant's zone
const fmtDate = (ymd: string) => { const p = (ymd || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : ymd; };
const fmtDateTime = (unix: number) => unix
  ? new Date(unix * 1000).toLocaleString(localeDate(), { timeZone: tz(), day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
  : '';
const fmtAmt = (n: number) => currencySymbol() + Number(n || 0).toLocaleString(localeNumber(), { maximumFractionDigits: 1 });
const round1 = (n: number) => Math.round((Number(n) || 0) * 10) / 10; // amounts limited to 1 decimal

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none',
};
const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };
// The top menu's Petty Cash coral, #D85A30 (Calem, report e981f781, 2nd opening: "Make Petty Cash into a bubble page
// as well and use all the same theme colors from the top menu for Petty Cash"): Save and the other filled buttons, the
// Banked Y / N, the From / To and Comments pickers' Other, the send icons and the auto-match note wear it,
// as the page's "+" does (see tabColor). They were the app's green accent. Inbound figures and From names, and the
// form's Cash Inbound choice, stay green: there it means money in, beside Outbound's red.
const PC_COLOR = tabColor('pettycash')!;

// ── Comments drop-down (Calem, report 43fad940) ─────────────────────────────
// His 13 reasons, word for word and in his order, then "Other" (free text, up to 50 characters).
// The ENGLISH label is what gets stored, so the Comments column and search read the same in every
// language — the key only translates the drop-down, and each en value equals its label.
// Calem, report 4297554d: the bracketed examples are only a grey hint in the list (key + '_hint')
// and are never stored. "Petty Cash (Refill)" is one name — its brackets are not a hint.
// Calem, report c327f92d: "DBD" is now "Company Related" (its key keeps the old name). A renamed
// reason keeps its old names in `legacy`: rows saved under one still read as this reason, shown by
// its new name, and are never rewritten.
// Report adf87a88: "Property Related" (land & building tax etc) joins them, after "License Related".
// Its 2nd opening, "Make Cash a capital C": "Petty cash (Refill)" is now "Petty Cash (Refill)", the old spelling a legacy name.
// Built when asked, not when this module loads: "Cash Payment to <brand>" needs the workspace brand, which arrives
// with /api/me after the bundle has loaded (at import it was always "the firm", so rows saved as "Cash Payment to
// <brand>" stopped reading as that reason). One list per brand, reused while the brand stays.
// ⛔ The label is the STORED text: with the brand "SLF" it is exactly the label rows were saved under.
type PcReason = { key: string; label: string; hint?: string; legacy?: string[] };
const buildReasons = (firm: string): PcReason[] => [
  { key: 'pc_reason_meals', label: 'Staff Meals, Drinks or Snacks', hint: '(weekly lunch, coffee, shakes, 7/11 etc)' },
  { key: 'pc_reason_supplies', label: 'Office Supplies or Repairs', hint: '(keys, batteries, lights etc)' },
  { key: 'pc_reason_office', label: 'Office Expenses', hint: '(phone, drinking water etc)' },
  { key: 'pc_reason_atm', label: 'ATM or 7/11 Deposit Fees' },
  { key: 'pc_reason_nacha', label: "Deposit to Nacha's Account" },   // tenant-literal-ok: the stored text of saved Comments (and the Banked question keys on it); becomes a workspace reason list once routes/pettycash.ts serves one
  { key: 'pc_reason_dbd', label: 'Company Related', hint: '(company changes, document updates etc)', legacy: ['DBD'] },
  { key: 'pc_reason_visa', label: 'Visa Related', hint: '(visa, extension, re-entry, 90 day report etc)' },
  { key: 'pc_reason_permit', label: 'Permit Related', hint: '(application, renewal, cancellation etc)' },
  { key: 'pc_reason_license', label: 'License Related', hint: '(food, alcohol, fitness etc)' },
  { key: 'pc_reason_property', label: 'Property Related', hint: '(land & building tax etc)' },
  { key: 'pc_reason_notary', label: 'Notary Public' },
  { key: 'pc_reason_postage', label: 'Postage & Delivery', hint: '(documents, passports etc)' },
  { key: 'pc_reason_cashslf', label: `Cash Payment to ${firm}`, hint: '(accounting, job, vat, property tax etc)' },
  { key: 'pc_reason_refill', label: 'Petty Cash (Refill)', legacy: ['Petty cash (Refill)'] },
];
let reasonsMemo: { firm: string; list: PcReason[] } | null = null;
const pcReasons = (): PcReason[] => {
  const firm = brandOr('the firm');
  if (!reasonsMemo || reasonsMemo.firm !== firm) reasonsMemo = { firm, list: buildReasons(firm) };
  return reasonsMemo.list;
};
// The stored label of one reason, by its key.
const reasonLabel = (key: string): string => pcReasons().find(r => r.key === key)?.label || '';
// A reason's name in the user's language; the brand rides along for a translation that names it.
const reasonVars = () => ({ brand: brandOr('the firm') });
// A stored first line → its reason. Rows saved before report 4297554d hold the long form,
// "label (hint)", so both read as the same reason — as does a former (legacy) name, in either form.
const reasonOf = (line: string) => {
  const s = String(line || '').trim();
  return pcReasons().find(r => [r.label, ...(r.legacy || [])].some(n => s === n || (!!r.hint && s === `${n} ${r.hint}`))) || null;
};
const PC_OTHER = '__other__';   // the drop-down value for "Other" — never a stored label
const PC_OTHER_MAX = 50;
// Every listed reason takes optional details (Calem, report 4297554d), stored as the line under the
// label — so edit, search and the Classify-as split still find the label on a line of its own.
const PC_DETAIL_MAX = 100;
// A no-match reason picked under "Classify as" in the Match column is appended to Comments as its
// own line (POST /no-match). It is not a Comments choice: split it off so the drop-down edits only
// the rest, and put it back last. Same line test as the server's stripReasonLine.
const splitComments = (comments: string, classify: string) => {
  const c = String(classify || '').trim();
  const lines = String(comments || '').split('\n');
  if (!c || !lines.some(l => l.trim() === c)) return { text: String(comments || '').trim(), classify: '' };
  return { text: lines.filter(l => l.trim() !== c).join('\n').trim(), classify: c };
};
// The Comments column (Calem, report 4297554d): a listed reason and its details read as ONE line,
// "Cash Payment to SLF – job 61107", and an older row's bracketed hint is left off. A Classify-as
// line, or any further line, stays on its own line after them. Display only: the stored text keeps
// label + "\n" + details.
// Calem, report 301a74b8: except the "Petty Cash Refill" line — the Match column's chip already names
// it, as does the Comments reason "Petty Cash (Refill)" when picked. It stays in the stored text, so
// undo and the edit form still find it.
const PC_REFILL_CLASSIFY = 'Petty Cash Refill';   // the Classify-as category of that name
const listComments = (e: PettyCashEntry) => {
  const { text, classify } = splitComments(e.comments || '', e.match?.noMatch ? e.match.reason || '' : '');
  const [head, detail = '', ...more] = text.split('\n');
  const r = reasonOf(head);
  const lines = r ? [[r.label, detail.trim()].filter(Boolean).join(' – '), ...more] : [text];
  return [...lines, classify === PC_REFILL_CLASSIFY ? '' : classify].map(l => l.trim()).filter(Boolean).join('\n');
};

// ── From / To picker (Calem, report 4297554d) ───────────────────────────────
// This world's active staff and clients (GET /petty-cash/parties), searchable, or "Other" — free text
// of at most 40 characters. Same fixed-overlay look as the Peak ClientPicker, so the modal's scroll
// can't clip the list. An older party that is not a listed name opens as "Other" with its text and,
// if left alone, is saved back unchanged — the same way Comments treats old text.
// The To picker is given its own `list` instead (Calem, report bb41eaae): the names already written in
// this ledger's To column (the same answer's `to`), shown as the column shows them, so an entry's To
// opens as itself; "Other" is for a new name.
// Each list holds a name once (3rd opening: "remove all double instances"). An entry written another
// way, "Banking " or "banking", opens with its listed name, "Banking", already picked (fromSame /
// toSame), and if left alone is saved back as it was written.
const PC_PARTY_MAX = 40;
type PcParties = { clients: string[]; staff: string[]; to: string[]; fromSame?: Record<string, string>; toSame?: Record<string, string> };
type Party = { v: string; other: boolean };   // other = "Other" was picked, so v is free text
// The name this picker lists for v: its own list's (To) or staff and clients' (From), else v itself.
const partyShown = (v: string, parties: PcParties | null, list?: string[]) => {
  const same: Record<string, string> = (list ? parties?.toSame : parties?.fromSame) || {};
  return (Object.prototype.hasOwnProperty.call(same, v) && same[v]) || v;
};
// "Other" = not one of the names this picker lists: its own list if it has one, else staff and clients.
const partyIsOther = (p: Party, parties: PcParties | null, list?: string[]) => {
  const v = partyShown(p.v, parties, list);
  return p.other || (!!p.v && !!parties && (list ? !list.includes(v) : !parties.staff.includes(v) && !parties.clients.includes(v)));
};

// onEdited (the To picker only, report 17188c3d): each name of the list gets a pencil (Rename) and an ×
// (Remove) while the mouse is on it. Remove asks first; Rename opens a box in the row. Both are saved
// at once for the whole ledger (PUT /petty-cash/parties/to), and onEdited then reloads the lists.
function PartyPicker({ label, parties, list, value, onChange, onEdited }: { label: string; parties: PcParties | null; list?: string[]; value: Party; onChange: (p: Party) => void; onEdited?: (action: 'rename' | 'remove') => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<{ left: number; top: number; width: number } | null>(null);
  const [hover, setHover] = useState('');         // the To name under the mouse
  const [renaming, setRenaming] = useState('');   // the To name whose Rename box is open
  const [newName, setNewName] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState('');
  useEffect(() => {
    if (!open) return;
    const recalc = () => { const el = triggerRef.current; if (!el) return; const r = el.getBoundingClientRect(); setRect({ left: r.left, top: r.bottom, width: r.width }); };
    recalc();
    window.addEventListener('scroll', recalc, true); window.addEventListener('resize', recalc);
    return () => { window.removeEventListener('scroll', recalc, true); window.removeEventListener('resize', recalc); };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const tgt = e.target as Node;
      if (triggerRef.current?.contains(tgt) || boxRef.current?.contains(tgt)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const isOther = partyIsOther(value, parties, list);
  const shown = partyShown(value.v, parties, list);   // an entry written another way shows its listed name
  const ql = q.trim().toLowerCase();
  const hit = (n: string) => !ql || n.toLowerCase().includes(ql);
  const names = (list || []).filter(hit);
  const staff = list ? [] : (parties?.staff || []).filter(hit);
  const clients = list ? [] : (parties?.clients || []).filter(hit);
  const pick = (p: Party) => { onChange(p); setOpen(false); };
  const opt = (key: string, text: string, p: Party, muted = false) => {
    const on = !muted && !isOther && shown === p.v;
    return (
      <div key={key} title={text} onClick={() => pick(p)}
        style={{ padding: '8px 10px', fontSize: 12.5, cursor: 'pointer', color: muted ? 'var(--text-tertiary)' : 'var(--text-primary)', fontWeight: on ? 700 : 500, background: on ? 'var(--bg-active)' : 'transparent', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        onMouseEnter={e => { if (!on) e.currentTarget.style.background = 'var(--bg-active)'; }}
        onMouseLeave={e => { if (!on) e.currentTarget.style.background = 'transparent'; }}>
        {text}
      </div>
    );
  };
  const head = (text: string) => (
    <div style={{ padding: '6px 10px 3px', fontSize: 10, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{text}</div>
  );
  const typed = q.trim().slice(0, PC_PARTY_MAX);

  // Rename / Remove one To name. The name picked in this form follows its rename; a removed one stays
  // as it is and shows as Other, as an older entry holding it does.
  const editTo = async (n: string, action: 'rename' | 'remove') => {
    if (editBusy) return;
    const to = newName.trim();
    if (action === 'rename' && (!to || to === n)) { setRenaming(''); return; }
    if (action === 'remove' && !window.confirm(t('pc_party_remove_confirm', { name: displayClientName(n) }))) return;
    setEditBusy(true); setEditErr('');
    try {
      const r = await api.pettyPartyTo({ name: n, action, ...(action === 'rename' ? { to } : {}) });
      if (action === 'rename' && !isOther && shown === n) onChange({ v: r?.name || to, other: false });
      setRenaming('');
      onEdited?.(action);
    } catch (e: any) { setEditErr(e.message); } finally { setEditBusy(false); }
  };
  const toolBtn: React.CSSProperties = { width: 22, height: 22, padding: 0, borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-sm)' };
  // A To name's row: the name (a click picks it) with the two tools over its right end on hover, or,
  // while it is being renamed, the box with Save / Cancel (Enter / Escape).
  const toRow = (n: string) => renaming === n ? (
    <div key={'t:' + n} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 6px 4px 10px', background: 'var(--bg-active)' }}>
      <input autoFocus value={newName} maxLength={PC_PARTY_MAX} disabled={editBusy} aria-label={t('pc_party_rename')}
        onChange={e => setNewName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') editTo(n, 'rename'); else if (e.key === 'Escape') setRenaming(''); }}
        style={{ ...inputStyle, width: 'auto', flex: 1, minWidth: 0, padding: '5px 8px', fontSize: 12.5 }} />
      <button type="button" title={t('save')} aria-label={t('save')} disabled={editBusy} onClick={() => editTo(n, 'rename')} style={toolBtn}>
        <i className="ti ti-check" style={{ fontSize: 12 }} aria-hidden />
      </button>
      <button type="button" title={t('cancel')} aria-label={t('cancel')} disabled={editBusy} onClick={() => setRenaming('')} style={toolBtn}>
        <i className="ti ti-x" style={{ fontSize: 12 }} aria-hidden />
      </button>
    </div>
  ) : (
    <div key={'t:' + n} style={{ position: 'relative' }} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(h => (h === n ? '' : h))}>
      {opt('t:' + n, displayClientName(n), { v: n, other: false })}
      {hover === n && !editBusy && (
        <span style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 4 }}>
          <button type="button" title={t('pc_party_rename')} aria-label={t('pc_party_rename')} onClick={() => { setRenaming(n); setNewName(n); setEditErr(''); }} style={toolBtn}>
            <i className="ti ti-pencil" style={{ fontSize: 12 }} aria-hidden />
          </button>
          <button type="button" title={t('pc_party_remove')} aria-label={t('pc_party_remove')} onClick={() => editTo(n, 'remove')} style={toolBtn}>
            <i className="ti ti-x" style={{ fontSize: 12 }} aria-hidden />
          </button>
        </span>
      )}
    </div>
  );

  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <button type="button" ref={triggerRef} onClick={() => { setQ(''); setRenaming(''); setEditErr(''); setOpen(o => !o); }}
        style={{ ...inputStyle, textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ color: isOther || value.v ? 'var(--text-primary)' : 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {isOther ? t('pc_reason_other') : value.v ? (list ? displayClientName(shown) : displayCompanyName(shown)) : t(list ? 'pc_party_to_pick' : 'pc_party_pick')}
        </span>
        <i className="ti ti-chevron-down" style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} aria-hidden />
      </button>
      {open && rect && (
        <div ref={boxRef} style={{ position: 'fixed', left: rect.left, top: rect.top + 2, width: rect.width, zIndex: 1001, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-md)', display: 'flex', flexDirection: 'column', maxHeight: 320 }}>
          <div style={{ padding: 8, borderBottom: '1px solid var(--border)' }}>
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={t(list ? 'pc_search' : 'pc_party_search')}
              onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }} style={inputStyle} />
            {editErr && <div style={{ fontSize: 11.5, color: '#E24B4A', marginTop: 6 }}>{editErr}</div>}
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {opt('none', '—', { v: '', other: false }, true)}
            {/* The To list reads as the To column does; the pick and the search stay on the name as written. */}
            {names.length > 0 && head(t('pc_party_to_head'))}
            {names.map(n => (onEdited ? toRow(n) : opt('t:' + n, displayClientName(n), { v: n, other: false })))}
            {staff.length > 0 && head(t('pc_party_staff'))}
            {staff.map(n => opt('s:' + n, n, { v: n, other: false }))}
            {clients.length > 0 && head(t('pc_party_clients'))}
            {/* A company shows without its legal form (Calem, report fcc6685f); the pick and the
                search stay on the name as filed, which is what the entry records. */}
            {clients.slice(0, 80).map(n => opt('c:' + n, displayCompanyName(n), { v: n, other: false }))}
            {parties && !names.length && !staff.length && !clients.length && <div style={{ padding: 10, fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pc_party_none')}</div>}
          </div>
          {/* "Other" starts from the search words, if any; otherwise it keeps an Other text already there. */}
          <button type="button" onClick={() => pick({ v: typed || (isOther ? value.v : ''), other: true })}
            style={{ border: 'none', borderTop: '1px solid var(--border)', padding: 10, background: 'transparent', color: PC_COLOR, fontWeight: 700, fontSize: 12.5, cursor: 'pointer', textAlign: 'left' }}>
            <i className="ti ti-pencil" style={{ marginRight: 6 }} aria-hidden />{t('pc_reason_other')}{typed ? `: “${typed}”` : ''}
          </button>
        </div>
      )}
      {isOther && (<>
        <input value={value.v} onChange={e => onChange({ v: e.target.value, other: true })} maxLength={PC_PARTY_MAX} autoFocus={value.other} style={{ ...inputStyle, marginTop: 6 }} />
        <div style={{ fontSize: 11, color: value.v.length > PC_PARTY_MAX ? '#E24B4A' : 'var(--text-tertiary)', textAlign: 'right', marginTop: 3 }}>{value.v.length}/{PC_PARTY_MAX}</div>
      </>)}
    </div>
  );
}

// ── Comments picker (Calem, report 4297554d) ────────────────────────────────
// The list shows each reason's name with its bracketed examples after it in grey; the box shows
// only the name, which is the part that is stored. "Other" sits last. Same fixed-overlay look as the
// From / To picker, so the modal's scroll can't clip the list — and it opens upwards when the room
// below the box is short, since Comments sits low in the form.
function ReasonPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<{ left: number; top: number; bottom: number; width: number } | null>(null);
  useEffect(() => {
    if (!open) return;
    const recalc = () => { const el = triggerRef.current; if (!el) return; const r = el.getBoundingClientRect(); setRect({ left: r.left, top: r.top, bottom: r.bottom, width: r.width }); };
    recalc();
    window.addEventListener('scroll', recalc, true); window.addEventListener('resize', recalc);
    return () => { window.removeEventListener('scroll', recalc, true); window.removeEventListener('resize', recalc); };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const tgt = e.target as Node;
      if (triggerRef.current?.contains(tgt) || boxRef.current?.contains(tgt)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const picked = pcReasons().find(r => r.label === value);
  const pick = (v: string) => { onChange(v); setOpen(false); };
  const opt = (key: string, v: string, name: string, hint = '', muted = false) => {
    const on = !muted && value === v;
    return (
      <div key={key} onClick={() => pick(v)}
        style={{ padding: '8px 10px', fontSize: 12.5, lineHeight: 1.4, cursor: 'pointer', color: muted ? 'var(--text-tertiary)' : 'var(--text-primary)', fontWeight: on ? 700 : 500, background: on ? 'var(--bg-active)' : 'transparent' }}
        onMouseEnter={e => { if (!on) e.currentTarget.style.background = 'var(--bg-active)'; }}
        onMouseLeave={e => { if (!on) e.currentTarget.style.background = 'transparent'; }}>
        {name}{hint && <span style={{ marginLeft: 4, fontWeight: 400, color: 'var(--text-tertiary)' }}>{hint}</span>}
      </div>
    );
  };
  const below = rect ? window.innerHeight - rect.bottom - 8 : 0;
  const above = rect ? rect.top - 8 : 0;
  const up = below < 240 && above > below;

  return (
    <>
      <button type="button" ref={triggerRef} onClick={() => setOpen(o => !o)} onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
        style={{ ...inputStyle, textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ color: value ? 'var(--text-primary)' : 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value === PC_OTHER ? t('pc_reason_other') : picked ? t(picked.key, reasonVars()) : t('pc_reason_pick')}
        </span>
        <i className="ti ti-chevron-down" style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} aria-hidden />
      </button>
      {open && rect && (
        <div ref={boxRef} style={{ position: 'fixed', left: rect.left, width: rect.width, ...(up ? { bottom: window.innerHeight - rect.top + 2 } : { top: rect.bottom + 2 }), maxHeight: Math.min(360, up ? above : below), zIndex: 1001, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-md)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {opt('none', '', '—', '', true)}
            {pcReasons().map(r => opt(r.key, r.label, t(r.key, reasonVars()), r.hint ? t(`${r.key}_hint`) : ''))}
          </div>
          <button type="button" onClick={() => pick(PC_OTHER)}
            style={{ border: 'none', borderTop: '1px solid var(--border)', padding: 10, background: value === PC_OTHER ? 'var(--bg-active)' : 'transparent', color: PC_COLOR, fontWeight: 700, fontSize: 12.5, cursor: 'pointer', textAlign: 'left' }}>
            <i className="ti ti-pencil" style={{ marginRight: 6 }} aria-hidden />{t('pc_reason_other')}
          </button>
        </div>
      )}
    </>
  );
}

// ── Invoice look-up in the "Cash Payment to SLF" details (Calem, report 3f4ad763) ──────────────
// "When we receive a cash payment to SLF and the user starts typing the invoice number in the text box
// below, allow that invoice to pop up and be connected to the cash taken." On cash in under that reason
// the details box looks up what is typed among both firms' unpaid invoices and quotations — the Match
// panel's own search (GET /petty-cash/match-candidates), exact amounts first — in the same fixed overlay
// as the pickers above, opening upwards when the room below is short. A pick writes its number into the
// box, as typing it would, and is held while the number stays there: saving then opens the Match panel
// on it (see PettyCashView), and only that panel's confirm issues the receipt. Text that picks nothing
// is saved as text, as before.
type DocPick = { docKind: 'invoice' | 'quotation'; docId: string; docNo: string; clientName: string };
// An invoice's figures keep their 2 decimals, as in the Match panel; petty-cash amounts are 1-decimal.
const fmtDocAmt = (n: number) => moneyTight(n);

// The look-ups' list — DocTypeahead's, and DocNoTypeahead's below: fixed under its box, or over it when
// the room below is short, so the modal's scroll can't clip it; a grey heading, then the documents.
type DocRect = { left: number; top: number; bottom: number; width: number };
function DocList({ rect, boxRef, head, children }: { rect: DocRect; boxRef: React.RefObject<HTMLDivElement>; head: string; children: React.ReactNode }) {
  const below = window.innerHeight - rect.bottom - 8;
  const above = rect.top - 8;
  const up = below < 240 && above > below;
  return (
    <div ref={boxRef} style={{ position: 'fixed', left: rect.left, width: rect.width, ...(up ? { bottom: window.innerHeight - rect.top + 2 } : { top: rect.bottom + 2 }), maxHeight: Math.min(320, up ? above : below), zIndex: 1001, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-md)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '6px 10px 3px', fontSize: 10, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{head}</div>
      <div style={{ overflowY: 'auto', flex: 1 }}>{children}</div>
    </div>
  );
}
// One document in it: the number, the client, then a grey note. A click takes it.
const docRow = (key: string, no: string, client: string, note: string, onPick: () => void) => (
  <div key={key} onClick={onPick} title={`${no} · ${client}`}
    style={{ padding: '8px 10px', fontSize: 12.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-active)'; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
    <span style={{ fontWeight: 700, color: 'var(--text-primary)', flexShrink: 0 }}>{no}</span>
    <span style={{ flex: 1, minWidth: 0, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis' }}>· {client || '—'}</span>
    <span style={{ flexShrink: 0, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{note}</span>
  </div>
);

function DocTypeahead({ value, amount, picked, onChange, onPick }: { value: string; amount: number; picked: DocPick | null; onChange: (v: string) => void; onPick: (p: DocPick) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [cands, setCands] = useState<any[] | null>(null);   // the last answer; null while closed
  const inputRef = useRef<HTMLInputElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<{ left: number; top: number; bottom: number; width: number } | null>(null);
  const q = value.trim();
  // It looks from two characters on, and not while a pick is held: words typed after its number are notes.
  const show = open && q.length >= 2 && !picked;
  useEffect(() => {
    if (!show) return;
    const recalc = () => { const el = inputRef.current; if (!el) return; const r = el.getBoundingClientRect(); setRect({ left: r.left, top: r.top, bottom: r.bottom, width: r.width }); };
    recalc();
    window.addEventListener('scroll', recalc, true); window.addEventListener('resize', recalc);
    return () => { window.removeEventListener('scroll', recalc, true); window.removeEventListener('resize', recalc); };
  }, [show]);
  useEffect(() => {
    if (!show) return;
    const onDown = (e: MouseEvent) => {
      const tgt = e.target as Node;
      if (inputRef.current?.contains(tgt) || boxRef.current?.contains(tgt)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [show]);
  // Asked once typing pauses; an answer to older words is dropped. The property-tax and tax cards the
  // same search returns are left out — this box names an invoice or a quotation. The list shows only
  // when something matches, so a plain note is typed undisturbed.
  useEffect(() => {
    if (!show) { setCands(null); return; }
    let alive = true;
    const tm = setTimeout(() => {
      api.pettyMatchCandidates(q, amount)
        .then((d: any) => { if (alive) setCands((d.candidates || []).filter((c: any) => c.kind === 'invoice' || c.kind === 'quotation')); })
        .catch(() => { if (alive) setCands([]); });
    }, 300);
    return () => { alive = false; clearTimeout(tm); };
  }, [show, q, amount]);

  const pick = (c: any) => {
    onPick({ docKind: c.kind, docId: String(c.id), docNo: String(c.doc_no), clientName: String(c.client_name || '') });
    setOpen(false);
  };

  return (
    <>
      <input ref={inputRef} value={value} maxLength={PC_DETAIL_MAX} placeholder={t('pc_doc_ph')}
        onChange={e => { onChange(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        onKeyDown={e => { if (e.key === 'Escape' || e.key === 'Tab') setOpen(false); }}
        style={{ ...inputStyle, marginTop: 6 }} />
      {show && rect && cands && cands.length > 0 && (
        <DocList rect={rect} boxRef={boxRef} head={t('pc_doc_head')}>
          {cands.map(c => docRow(c.kind + c.id, c.doc_no, displayClientName(c.client_name), `${t('bs_remaining')} ${fmtDocAmt(c.remaining)}`, () => pick(c)))}
        </DocList>
      )}
    </>
  );
}

// ── Quotation and invoice numbers in every other details box (Calem, report e981f781, 4th opening) ──
// "Whenever anybody searches for a quotation or an invoice in this text box, show them the number (as
// stored under quotations or invoice) as they start typing to click on." The details under every listed
// reason, cash in or out, and the "Other" box look the word being typed up — from its 2nd character —
// among this world's quotation and invoice numbers of any status (GET /petty-cash/doc-numbers), in the
// same list as DocTypeahead. A click puts the number in place of that word and it stays plain text:
// nothing is linked and no Match panel opens. Cash in as "Cash Payment to SLF" keeps DocTypeahead.
type DocNo = { id: string; kind: 'quotation' | 'invoice'; doc_no: string; client_name: string; issue_date: string };

function DocNoTypeahead({ value, maxLength, placeholder, onChange }: { value: string; maxLength: number; placeholder?: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState<DocNo[] | null>(null);   // the last answer; null while closed
  const inputRef = useRef<HTMLInputElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<DocRect | null>(null);
  // The word being typed is what follows the last space, so a number typed after a note is looked up too.
  const word = value.slice(value.search(/\S*$/));
  const show = open && word.length >= 2;
  useEffect(() => {
    if (!show) return;
    const recalc = () => { const el = inputRef.current; if (!el) return; const r = el.getBoundingClientRect(); setRect({ left: r.left, top: r.top, bottom: r.bottom, width: r.width }); };
    recalc();
    window.addEventListener('scroll', recalc, true); window.addEventListener('resize', recalc);
    return () => { window.removeEventListener('scroll', recalc, true); window.removeEventListener('resize', recalc); };
  }, [show]);
  useEffect(() => {
    if (!show) return;
    const onDown = (e: MouseEvent) => {
      const tgt = e.target as Node;
      if (inputRef.current?.contains(tgt) || boxRef.current?.contains(tgt)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [show]);
  // Asked once typing pauses; an answer to an older word is dropped. A number already typed out in full
  // is not offered back, so the list shows only when it has something to add and plain notes go undisturbed.
  useEffect(() => {
    if (!show) { setDocs(null); return; }
    let alive = true;
    const tm = setTimeout(() => {
      api.pettyDocNumbers(word)
        .then(d => { if (alive) setDocs((d.docs || []).filter(x => x.doc_no !== word)); })
        .catch(() => { if (alive) setDocs([]); });
    }, 300);
    return () => { alive = false; clearTimeout(tm); };
  }, [show, word]);
  const pick = (d: DocNo) => { onChange(value.slice(0, value.length - word.length) + d.doc_no); setOpen(false); };

  return (
    <>
      <input ref={inputRef} value={value} maxLength={maxLength} placeholder={placeholder}
        onChange={e => { onChange(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        onKeyDown={e => { if (e.key === 'Escape' || e.key === 'Tab') setOpen(false); }}
        style={{ ...inputStyle, marginTop: 6 }} />
      {show && rect && docs && docs.length > 0 && (
        <DocList rect={rect} boxRef={boxRef} head={t('pc_docno_head')}>
          {docs.map(d => docRow(d.kind + d.id, d.doc_no, displayClientName(d.client_name), t(d.kind === 'quotation' ? 'pk_quotation' : 'pk_invoice'), () => pick(d)))}
        </DocList>
      )}
    </>
  );
}

// ── Add / edit / delete form (modal) ────────────────────────────────────────
// Calem, report 4297554d: Cash Inbound or Cash Outbound is asked FIRST — neither is picked for a new
// entry and nothing else shows until one is — then only that side: Amount + From, or Amount + To.
// The date and time are automatic, and Banked is asked only for cash out as "Deposit to Nacha's Account".
// Asked at render (see pcReasons): the cash-payment label carries the brand.
const PC_NACHA_KEY = 'pc_reason_nacha';
const PC_CASHSLF_KEY = 'pc_reason_cashslf';
const fmtTime = (unix: number) => unix
  ? new Date(unix * 1000).toLocaleTimeString('en-GB', { timeZone: tz(), hour: '2-digit', minute: '2-digit', hour12: false })
  : '';

// onToRenamed: a Rename in the To picker has rewritten the To column, so the page's list reloads behind the form.
function EntryForm({ entry, lastClosing, onClose, onSaved, onToRenamed }: { entry: PettyCashEntry | null; lastClosing: number; onClose: () => void; onSaved: (link?: { entryId: string; doc: DocPick }) => void; onToRenamed?: () => void }) {
  const { t } = useTranslation();
  const isNew = !entry;
  // An edit opens on the side that holds the money. An older entry with money on BOTH sides opens on
  // neither and shows both, saved as it is, until a side is picked — never silently zero half of it.
  const origIn = Number(entry?.inbound) || 0, origOut = Number(entry?.outbound) || 0;
  const [dir, setDir] = useState<'' | 'in' | 'out'>(origIn && !origOut ? 'in' : origOut && !origIn ? 'out' : '');
  const both = !dir && !!origIn && !!origOut;
  const showIn = dir === 'in' || both, showOut = dir === 'out' || both;
  const [inbound, setInbound] = useState(String(entry?.inbound ?? 0));
  const [outbound, setOutbound] = useState(String(entry?.outbound ?? 0));
  const origFrom = entry?.from_party || '', origTo = entry?.to_party || '';
  const [fromParty, setFromParty] = useState<Party>({ v: origFrom, other: false });
  const [toParty, setToParty] = useState<Party>({ v: origTo, other: false });
  const [parties, setParties] = useState<PcParties | null>(null);
  const loadParties = () => { api.pettyParties().then(setParties).catch(() => { /* the picker still offers Other */ }); };
  useEffect(loadParties, []);   // eslint-disable-line react-hooks/exhaustive-deps
  // After a Rename or Remove in the To picker (report 17188c3d) its lists reload.
  const toEdited = (action: 'rename' | 'remove') => { loadParties(); if (action === 'rename') onToRenamed?.(); };
  // To lists the names already written in the To column (Calem, report bb41eaae); From keeps staff and clients.
  const toNames = parties?.to || [];
  // A new entry is stamped now: txn_date = today in the tenant's zone, the minute = the row's created_at.
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    if (!isNew) return;
    const iv = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 10000);
    return () => clearInterval(iv);
  }, [isNew]);
  const stamp = isNew ? fmtDateTime(now) : [fmtDate(entry!.txn_date), fmtTime(entry!.created_at)].filter(Boolean).join(', ');
  // Comments = one of pcReasons(), or "Other" + its text. An old free-text comment opens as "Other"
  // with its text, and if left untouched it is saved back byte-for-byte (it may run past 50).
  // A comment whose first line is a listed reason (or the long form older rows hold) opens with it
  // picked and the rest as its details; left untouched, it too is saved back as it was.
  const orig = splitComments(entry?.comments || '', entry?.match?.noMatch ? entry.match.reason || '' : '');
  const [origHead, ...origRest] = orig.text.split('\n');
  const origReason = reasonOf(origHead);
  const origPick = !orig.text ? '' : origReason ? origReason.label : PC_OTHER;
  const origDetail = origReason ? origRest.join('\n').trim() : '';
  const [reason, setReason] = useState(origPick);
  const [other, setOther] = useState(origPick === PC_OTHER ? orig.text : '');
  const [detail, setDetail] = useState(origDetail);
  const listed = !!reason && reason !== PC_OTHER;   // a listed reason is picked — it takes details
  const commentsChanged = reason !== origPick || (reason === PC_OTHER && other !== orig.text) || (listed && detail !== origDetail);
  // Cash in as "Cash Payment to SLF": the details look up invoices (DocTypeahead). A pick counts while
  // its number is still in the details, and never on an entry already fully matched — the Match column
  // holds that one.
  const [docPick, setDocPick] = useState<DocPick | null>(null);
  const canLink = dir === 'in' && reason === reasonLabel(PC_CASHSLF_KEY) && entry?.match_state !== 'matched';
  const linkPick = canLink && docPick && detail.includes(docPick.docNo) ? docPick : null;
  const [banked, setBanked] = useState(entry ? entry.banked === 1 : false);
  // Banked is asked only for cash out as "Deposit to Nacha's Account"; every other entry saves N.
  const nacha = dir === 'out' && reason === reasonLabel(PC_NACHA_KEY);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  // Opening = previous entry's closing (read-only). Closing = opening + inbound − outbound (auto),
  // counting only the side that will be saved — the other goes in as 0.
  const opening = isNew ? round1(lastClosing) : round1(entry!.opening_balance || 0);
  const closing = round1(opening + (showIn ? Number(inbound) || 0 : 0) - (showOut ? Number(outbound) || 0 : 0));

  // A party left as it was is stored as it was (an older text may run past 40); a new one trimmed.
  // The side not saved keeps its party only if it never held money: money moved off a side takes
  // its party with it.
  const partyOut = (p: Party, was: string) => (p.v === was ? was : p.v.trim());
  const fromOut = showIn ? partyOut(fromParty, origFrom) : origIn ? '' : origFrom;
  const toOut = showOut ? partyOut(toParty, origTo) : origOut ? '' : origTo;

  // The picked reason (or the Other text), its details if any, then the Classify-as line, if there
  // is one, kept last.
  const comments = commentsChanged
    ? [reason === PC_OTHER ? other.trim() : reason, listed ? detail.trim() : '', orig.classify].filter(Boolean).join('\n')
    : (entry?.comments || '');
  const payload = () => ({
    // New: today in the tenant's zone (the minute is the row's created_at). An edit keeps its own date.
    date: isNew ? todayLocal() : entry!.txn_date,
    openingBalance: opening, inbound: showIn ? round1(Number(inbound) || 0) : 0, fromParty: fromOut,
    closingBalance: closing, outbound: showOut ? round1(Number(outbound) || 0) : 0, toParty: toOut, comments,
    banked: nacha && banked,
    // Only when "Other" was picked or its text edited — the server holds it to 1–50 characters.
    ...(commentsChanged && reason === PC_OTHER ? { otherComment: other.trim() } : {}),
  });

  const save = async () => {
    if (!dir && !both) { setErr(t('pc_dir_pick')); return; }
    // An "Other" party needs its name, at most 40 characters, unless it is an older text left alone.
    const badOther = (shown: boolean, p: Party, out: string, was: string, list?: string[]) =>
      shown && partyIsOther(p, parties, list) && (!out || (out !== was && out.length > PC_PARTY_MAX));
    if (badOther(showIn, fromParty, fromOut, origFrom) || badOther(showOut, toParty, toOut, origTo, toNames)) { setErr(t('pc_party_other_req')); return; }
    if (commentsChanged && reason === PC_OTHER && (!other.trim() || other.trim().length > PC_OTHER_MAX)) { setErr(t('pc_reason_other_req')); return; }
    // A reason's details are optional; older ones left alone may run past 100, edited ones may not.
    if (commentsChanged && listed && detail.trim().length > PC_DETAIL_MAX) { setErr(t('pc_reason_detail_max')); return; }
    setSaving(true); setErr('');
    try {
      let id = entry?.id || '';
      if (isNew) id = String((await api.createPettyCash(payload()))?.id || '');
      else await api.updatePettyCash(entry!.id, payload());
      // A document picked in the details goes with the save: the page opens the Match panel on it.
      onSaved(linkPick && id ? { entryId: id, doc: linkPick } : undefined); onClose();
    } catch (e: any) { setErr(e.message); } finally { setSaving(false); }
  };
  const del = async () => {
    if (!entry || !window.confirm(t('pc_confirm_delete'))) return;
    setSaving(true);
    try { await api.deletePettyCash(entry.id); onSaved(); onClose(); }
    catch (e: any) { setErr(e.message); setSaving(false); }
  };

  const moneyEdit = (label: string, val: string, setVal: (s: string) => void) => (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ position: 'relative' }}>
        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'var(--text-tertiary)' }}>{currencySymbol()}</span>
        <input type="number" step="0.1" value={val} onChange={e => setVal(e.target.value)} onBlur={e => setVal(String(round1(Number(e.target.value) || 0)))} style={{ ...inputStyle, paddingLeft: 24 }} />
      </div>
    </div>
  );
  const moneyRO = (label: string, value: number) => (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ position: 'relative' }}>
        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'var(--text-tertiary)' }}>{currencySymbol()}</span>
        <input value={value} readOnly tabIndex={-1} style={{ ...inputStyle, paddingLeft: 24, background: 'var(--c-gray-100)', color: 'var(--text-secondary)', cursor: 'not-allowed' }} />
      </div>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 460, maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: 24, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>
          {isNew ? t('pc_add') : t('edit')}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Inbound or outbound first; the rest of the form follows the choice. Each choice is ringed
              1px in its own colour (Calem, report 126f21bf: "Reduce all borders … for all bubbles from
              2 px to 1 px"); was 2px. */}
          <div style={{ display: 'flex', gap: 10 }}>
            {([['in', 'pc_dir_in', 'var(--accent)', 'var(--accent-bg)', 'ti ti-arrow-down'], ['out', 'pc_dir_out', '#E24B4A', '#FCEBEB', 'ti ti-arrow-up']] as const).map(([k, label, color, tint, icon]) => {
              const on = dir === k;
              return (
                <button key={k} type="button" onClick={() => { setDir(k); setErr(''); }}
                  style={{ flex: 1, padding: '10px 12px', borderRadius: 999, border: `1px solid ${color}`, background: on ? color : tint, color: on ? '#fff' : color, opacity: dir && !on ? 0.55 : 1, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                  <i className={icon} style={{ fontSize: 15, marginRight: 6 }} aria-hidden />{t(label)}
                </button>
              );
            })}
          </div>
          {!dir && !both && !err && <div style={{ fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'center' }}>{t('pc_dir_pick')}</div>}
          {(dir || both) && (<>
            {both && <div style={{ fontSize: 12, color: '#B45309', background: '#FEF3C7', padding: '8px 12px', borderRadius: 'var(--radius-md)', lineHeight: 1.5 }}>{t('pc_dir_both')}</div>}
            <div>
              <label style={labelStyle}>{t('pc_date')}</label>
              <input value={stamp} readOnly tabIndex={-1} style={{ ...inputStyle, background: 'var(--c-gray-100)', color: 'var(--text-secondary)', cursor: 'not-allowed' }} />
            </div>
            {moneyRO(t('pc_opening'), opening)}
            {showIn && moneyEdit(both ? t('pc_inbound') : t('pc_amount'), inbound, setInbound)}
            {showIn && <PartyPicker label={t('pc_from')} parties={parties} value={fromParty} onChange={setFromParty} />}
            {showOut && moneyEdit(both ? t('pc_outbound') : t('pc_amount'), outbound, setOutbound)}
            {showOut && <PartyPicker label={t('pc_to')} parties={parties} list={toNames} value={toParty} onChange={setToParty} onEdited={toEdited} />}
            {moneyRO(t('pc_closing'), closing)}
            <div>
              <label style={labelStyle}>{t('pc_comments')}</label>
              {/* Picking a different reason clears its details, and any invoice picked in them. */}
              <ReasonPicker value={reason} onChange={v => { if (v !== reason) { setReason(v); setDetail(''); setDocPick(null); } }} />
              {/* Every box here offers quotation and invoice numbers as they are typed (DocNoTypeahead), except the
                  details of cash in as "Cash Payment to SLF", which look up unpaid ones to match (DocTypeahead). */}
              {reason === PC_OTHER && (<>
                <DocNoTypeahead value={other} maxLength={PC_OTHER_MAX} onChange={setOther} />
                <div style={{ fontSize: 11, color: other.length > PC_OTHER_MAX ? '#E24B4A' : 'var(--text-tertiary)', textAlign: 'right', marginTop: 3 }}>{other.length}/{PC_OTHER_MAX}</div>
              </>)}
              {listed && (<>
                {canLink
                  ? <DocTypeahead value={detail} amount={round1(Number(inbound) || 0)} picked={linkPick} onChange={setDetail} onPick={p => { setDocPick(p); setDetail(p.docNo); }} />
                  : <DocNoTypeahead value={detail} maxLength={PC_DETAIL_MAX} placeholder={t('pc_reason_detail')} onChange={setDetail} />}
                <div style={{ fontSize: 11, color: detail.length > PC_DETAIL_MAX ? '#E24B4A' : 'var(--text-tertiary)', textAlign: 'right', marginTop: 3 }}>{detail.length}/{PC_DETAIL_MAX}</div>
                {linkPick && (
                  <div style={{ fontSize: 11.5, color: PC_COLOR, lineHeight: 1.45 }}>
                    <i className="ti ti-link" style={{ marginRight: 5 }} aria-hidden />{t('pc_doc_picked', { no: linkPick.docNo, client: displayClientName(linkPick.clientName) || '—' })}
                  </div>
                )}
              </>)}
            </div>
            {nacha && (
              <div>
                <label style={labelStyle}>{t('pc_banked')}</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[false, true].map(v => (
                    <button key={String(v)} onClick={() => setBanked(v)} style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-md)', border: '1px solid', borderColor: banked === v ? PC_COLOR : 'var(--border-strong)', background: banked === v ? PC_COLOR : 'transparent', color: banked === v ? '#fff' : 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      {v ? 'Y' : 'N'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!isNew && (
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', borderTop: '1px solid var(--border)', paddingTop: 10, lineHeight: 1.6 }}>
                <div>{t('pc_created')}: {entry!.created_by_name || '—'} · {fmtDateTime(entry!.created_at)}</div>
                <div>{t('pc_updated')}: {entry!.updated_by_name || '—'} · {fmtDateTime(entry!.updated_at)}</div>
              </div>
            )}
          </>)}
          {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)' }}>{err}</div>}

          <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
            {!isNew && (
              <button onClick={del} disabled={saving} style={{ padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color: '#E24B4A', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <i className="ti ti-trash" style={{ fontSize: 14, marginRight: 6 }} aria-hidden />{t('delete')}
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginRight: 8 }}>{t('cancel')}</button>
            <button onClick={save} disabled={saving} style={{ padding: '8px 16px', borderRadius: 'var(--radius-md)', border: 'none', background: PC_COLOR, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{saving ? t('login_wait') : t('save')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Reusable Petty Cash view — used by BOTH the standalone module page and the
// ── portal Dashboard tab (same code, same design). Fills its flex parent.
// ── Modal shell styles (the undo-with-job popup's) ──────────────────────────
const pcOverlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 16 };
const pcCard: React.CSSProperties = { background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: 460, maxHeight: '90vh', display: 'flex', flexDirection: 'column' };
const pcHead: React.CSSProperties = { fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', padding: '16px 20px 8px' };
const pcBtn = (kind: 'primary' | 'ghost' | 'danger'): React.CSSProperties => ({
  padding: '8px 16px', borderRadius: 'var(--radius-md)', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  background: kind === 'primary' ? PC_COLOR : kind === 'danger' ? '#E24B4A' : 'var(--c-gray-100)',
  color: kind === 'ghost' ? 'var(--text-secondary)' : '#fff',
});

// ── Undo-match confirmation that ALSO deletes the linked job (shows its details) ─────────────────
function UndoWithJobModal({ job, busy, onCancel, onConfirm }: { job: any; busy: boolean; onCancel: () => void; onConfirm: () => void }) {
  const { t } = useTranslation();
  const row = (k: string, v: React.ReactNode) => (<div style={{ display: 'flex', gap: 10, fontSize: 12.5, padding: '3px 0' }}><span style={{ width: 96, color: 'var(--text-tertiary)', flexShrink: 0 }}>{k}</span><span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{v}</span></div>);
  return (
    <div style={pcOverlay} onClick={busy ? undefined : onCancel}>
      <div style={{ ...pcCard, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div style={pcHead}><i className="ti ti-alert-triangle" style={{ marginRight: 6, color: '#E24B4A' }} />{t('pc_undo_job_title')}</div>
        <div style={{ padding: '4px 20px 8px' }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>{t('pc_undo_job_body')}</div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '10px 12px', background: 'var(--c-gray-50)' }}>
            {row(t('pc_job_no'), <strong>#{Number(job.job_no).toLocaleString()}</strong>)}
            {row(t('pc_job_specific'), jobTitleLabel(job.specific_job) || '—')}
            {row(t('pc_job_client'), `${job.first_name || ''} ${job.last_name || ''}`.trim() || '—')}
            {row(t('pc_job_amount'), fmtAmt(job.line_net))}
            {row(t('bs_match'), job.process_level ? <LevelLabel v={job.process_level} /> : '—')}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '10px 20px 16px', borderTop: '1px solid var(--border)' }}>
          <button onClick={onCancel} disabled={busy} style={pcBtn('ghost')}>{t('cancel')}</button>
          <button onClick={onConfirm} disabled={busy} style={{ ...pcBtn('danger'), opacity: busy ? 0.6 : 1 }}>{busy ? t('login_wait') : t('pc_undo_job_confirm')}</button>
        </div>
      </div>
    </div>
  );
}

// A banked (Y) row's Outbound amount and To name read blue instead of red (Calem, report 24b22cda: blue at first,
// then orange #EA580C — "not enough contrast" — then pink #DB2777, then "Change banking back to blue"). The first
// fix's blue-700, the text blue of this file's blue chips: about 6.3:1 on the pale coral row #FDF6F3, where the
// orange was 3.3:1 and the pink 4.3:1. N rows stay red #E24B4A.
const PC_BANKED_BLUE = '#1D4ED8';
// Entry rows in the top menu's Petty Cash coral, two shades lighter (Calem, Bugify #1a85b4b0 — see rowTint). Were
// --c-gray-50, two shades lighter than the page's beige (Calem, report 24b22cda). Hover takes a card's own fill, this
// or PC_BANKED_BG, three shades darker, where it took it two shades lighter and before that turned the card white,
// --bg-active (Calem, report 28e90b5f — see the row).
// The coral is one shade lighter, not two, since report 0fb815da, 3rd opening ("Make the bubble background one shade
// darker for everything on the orange menu pages (petty cash)"): a shade darker, #FDF6F3 → #FBF1ED, with the banked
// cards and the search box a shade darker too (see PC_BANKED_BG and the search box). Down rowTint's own scale, not
// darken, which turns a fill grey. A hover follows it, #FBF1ED → #F8E7E0.
const PC_ROW_BG = rowTint('pettycash', 1)!;
// A banked (Y) entry's card is that coral two shades darker, so it stands apart from an N one by more than its blue
// words and tick (report 0dab4b3a: "Whenever there has been banking and the money has been banked, change the
// background of that bubble to two shades darker"). Down rowTint's own scale, as the Receipts list's firmBg:
// PC_ROW_BG is the box's wash a shade lighter, so this is the wash a shade darker, a shade darker than the top menu's
// Petty Cash box — #FBF1ED → #F9E7E1. It was the wash itself, #FAECE7, two shades darker than #FDF6F3, until both went
// a shade darker (report 0fb815da, 3rd opening — see PC_ROW_BG). Not darken, which heads for black: two of its shades
// would make this a mid grey. Only the fill: the card's edge, the tick and PC_BANKED_BLUE (about 5.6:1 on this, 6.0:1
// on PC_ROW_BG) stay as they are. A hover takes this fill three shades darker, #F9E7E1 → #F5D5CB, the blue about 4.9:1
// on it (report 28e90b5f — see the row).
const PC_BANKED_BG = rowTint('pettycash', -1)!;
// Each entry is a rounded card, built the way the Bank Statement builds its own (cardLine there) — Calem, report
// e981f781, 2nd opening: "Make Petty Cash into a bubble page as well and use all the same theme colors from the top menu
// for Petty Cash". The list was a grid, a thin grey line under the header and under every cell; of those only the
// header's is back (that report's 3rd opening — see th).
// The top menu's Petty Cash box made a card: its wash a shade lighter is the fill (PC_ROW_BG, on the row), its line
// the card's edge — TAB_GROUPS.petty.border, 1px as on the box — with the Bank and Jobs cards' 10px corners. The cells
// draw it: across the top and bottom of every cell (td), down the Date cell's left and the chevron cell's right, rounded
// at the card's four corners (edgeL / edgeR). Cards stand CARD_GAP apart (cardGap), 7px where it was 4 (Calem, report
// 5d3e1bce: "On all system pages (except tracking) increase the gaps between the bubbles by 3 pixels"), and CARD_INSET
// in from the list's
// edges — the table's padding — on the page's white, --bg-surface, set on the scroll box so it runs on under the last
// card (Calem, report ddf1ba0e, 3rd opening: "Change every page background to white"); it was the list's grey,
// --c-gray-50. The edge is a shade darker than the box's line since that opening ("darken up the bubble border on each
// page by one shade"): 0.51 → 0.63, a quarter of the way on to the solid #D85A30 (see darken), so on white it goes
// #EBAB95 → #E6977D. The box and the search box keep the box's line.
const cardLine = `1px solid ${darken(TAB_GROUPS.petty.border, 1)}`;
const CARD_R = 10;
const CARD_GAP = 7;
const CARD_INSET = 17;

export function PettyCashView() {
  const { t } = useTranslation();
  useDocSeries();   // the document-number prefixes the Match column reads (utils/docSeries.ts)
  const [entries, setEntries] = useState<PettyCashEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastClosing, setLastClosing] = useState(0);
  const [loading, setLoading] = useState(true);
  useFocusRow(!loading);   // deep link from the tasks bell: scroll to & flash that entry
  // Badge drill-down (?badge=1): show ONLY the unmatched incoming rows the red badge counts.
  const { badgeOnly, clearBadge } = useBadgeOnly();
  const [q, setQ] = useState('');
  // The toolbar's Outbound / Inbound / Banking bubble that is lit, if any ('' = the whole list).
  const [kind, setKind] = useState<'' | 'out' | 'in' | 'banked'>('');
  const [editing, setEditing] = useState<PettyCashEntry | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [err, setErr] = useState('');
  const [selectedMatch, setSelectedMatch] = useState<PettyCashEntry | null>(null);
  // The invoice / quotation picked in the entry form's details (Calem, report 3f4ad763): the Match panel
  // opens on it, as its suggestion, for the entry just saved. The receipt is still issued only there.
  const [formPick, setFormPick] = useState<DocPick | null>(null);
  // VAT / WHT matching in Petty Cash (owner 2026-08-28): the operator picks the tax kind, the panel
  // then lists that client's uncollected months — identical to Bank Statement, and it credits the
  // client's tax card (the old "VAT w/o Receipt" label credited nobody and could not be emailed).
  const [matchKind, setMatchKind] = useState<'vat' | 'wht' | 'pnd51' | undefined>(undefined);
  const [taxEmailFor, setTaxEmailFor] = useState<string | null>(null);
  const [rcEmailFor, setRcEmailFor] = useState<string | null>(null);   // receipt id in the email editor
  const [matching, setMatching] = useState(false);
  const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);
  const [undoJob, setUndoJob] = useState<{ entry: PettyCashEntry; job: any } | null>(null);  // undo + delete-job confirm
  const [undoBusy, setUndoBusy] = useState(false);
  const [menuFor, setMenuFor] = useState<{ id: string; x: number; y: number } | null>(null);   // Match/No-match dropdown
  const [customFor, setCustomFor] = useState<PettyCashEntry | null>(null);                       // custom no-match reason modal
  const [customReason, setCustomReason] = useState('');
  // Same prompt as the Bank Statement side: a notary fee is recorded against the person it was done
  // for, captured as two fields so the format stays consistent and searchable.
  const [notaryFor, setNotaryFor] = useState<PettyCashEntry | null>(null);
  const [notaryFirst, setNotaryFirst] = useState('');
  const [notaryLast, setNotaryLast] = useState('');
  const [noMatchBusy, setNoMatchBusy] = useState<string | null>(null);

  // quiet=true → refresh data WITHOUT the loading spinner: the table stays mounted and React only
  // re-renders the cells whose data changed. Used after per-row actions (match / no-match / undo)
  // so the page doesn't visibly "reload". Full spinner only on mount / refresh / paging.
  // Persist one tax family's paid date, optimistically — same contract as the Bank Statement's.
  const setTaxPaid = async (e: any, taxKey: string, paidOn: string) => {
    const before = entries;
    setEntries(es => es.map((x: any) => {
      if (x.id !== e.id) return x;
      const marks = (x.collectionPaid || []).filter((m: any) => m.taxKey !== taxKey);
      return { ...x, collectionPaid: paidOn ? [...marks, { taxKey, paidOn, byName: '' }] : marks };
    }));
    // Roll back to the truth, then rethrow: <CollectionCell> shows an in-place NOT SAVED +
    // retry, which an operator cannot dismiss by accident the way an alert() could be.
    try { await api.pettySetCollectionPaid(e.id, taxKey, paidOn); }
    catch (er: any) { setEntries(before); throw er; }
  };

  const [pageSize, setPageSize] = useState(getPageSize());
  const load = async (p = page, quiet = false) => {
    if (!quiet) setLoading(true);
    setErr('');
    try {
      const res = await api.getPettyCash(p, badgeOnly, q, 0, kind);
      setEntries(res.rows); setTotal(res.total); setPage(res.page); setLastClosing(res.lastClosing || 0);
    } catch (e: any) { setErr(e.message); }
    finally { if (!quiet) setLoading(false); }
  };

  // One-time auto-match of pending inbound entries (server enforces once-only).
  const runAutomatch = async () => {
    setMatching(true);
    try {
      for (let i = 0; i < 15; i++) {
        const r: any = await api.pettyAutomatchPending();
        if (r.processed > 0) await load(page, true);
        if (!r.remaining || r.processed === 0) break;
      }
    } catch { /* non-blocking */ } finally { setMatching(false); }
  };

  // The receipt email opens in the same editor Peak receipts use (owner 2026-10-03: every outbound
  // email opens an editor first) — prefilled exactly as the one-click send built it, sent through the
  // same receipt send path and gates. No name is passed, so the greeting is the receipt's client as before.
  const emailMatched = (e: PettyCashEntry) => {
    if (!e.receipt_id) return;
    setRcEmailFor(e.receipt_id);
  };
  const undoMatch = async (e: PettyCashEntry) => {
    // If a job was created from this credit, confirm deleting it too (with its details) first.
    if ((e as any).jobNo) {
      try { const r: any = await api.pettyGetJob(e.id); if (r.job) { setUndoJob({ entry: e, job: r.job }); return; } } catch { /* fall back to plain confirm */ }
    }
    if (!window.confirm(t('bs_unmatch_confirm'))) return;
    try { await api.pettyUnmatch(e.id); load(page, true); } catch (er: any) { alert(er.message); }
  };
  const confirmUndoWithJob = async () => {
    if (!undoJob) return;
    setUndoBusy(true);
    try { await api.pettyUnmatch(undoJob.entry.id); setUndoJob(null); load(page, true); }
    catch (er: any) { alert(er.message); }
    finally { setUndoBusy(false); }
  };
  const openPdf = async (id: string) => { if (pdfBusyId) return; setPdfBusyId(id); try { await api.openReceiptPdf(id); } catch { /* ignore */ } finally { setPdfBusyId(null); } };

  // The 8 classification categories (user-specified 2026-07-22, exact English labels — do not
  // reword). Selecting one tags the row via the no-match mechanism (reason = the label).
  //  tone: gray = internal funds · blue = our service income, receipt still to be issued
  //        red = pass-through money (taxes/fees we forward), no receipt from us
  //  panel: true (w/ Receipt) → after tagging, ALSO open the match panel to issue the receipt.
  const PC_CATEGORIES: { label: string; icon: string; tone: 'gray' | 'blue' | 'red'; panel?: boolean; ask?: 'notary' }[] = [
    { label: 'Petty Cash Refill', icon: 'ti ti-wallet', tone: 'gray' },
    { label: 'Accounting w/ Receipt', icon: 'ti ti-receipt', tone: 'blue', panel: true },
    { label: 'Job w/ Receipt', icon: 'ti ti-briefcase', tone: 'blue', panel: true },
    { label: 'Job w/o Receipt', icon: 'ti ti-briefcase', tone: 'red' },
    { label: 'Property Tax w/o Receipt', icon: 'ti ti-home', tone: 'red' },
    { label: 'Property Tax Receipt w/o Receipt', icon: 'ti ti-file-invoice', tone: 'red' },
    { label: 'WHT w/o Receipt', icon: 'ti ti-percentage', tone: 'red' },
    { label: 'VAT w/o Receipt', icon: 'ti ti-coin', tone: 'red' },
    // Notary fees are paid on to the notary office — money we forward, so no receipt from us
    // (owner 2026-08-11).
    { label: 'Notary Public w/o Receipt', icon: 'ti ti-stamp', tone: 'red', ask: 'notary' },
  ];
  const TONE = {
    gray: { bg: '#F2F4F7', border: '#E4E7EC', text: '#475467' },
    blue: { bg: '#EFF6FF', border: '#BFDBFE', text: '#1D4ED8' },
    red: { bg: '#FEF3F2', border: '#FECDCA', text: '#B42318' },
  } as const;
  // Prefix match, not equality: a category may now carry a suffix (" : Surname, Firstname"), and an
  // exact-match lookup would drop the chip back to the generic red ⊘ with no icon.
  const catForReason = (reason: string) =>
    PC_CATEGORIES.find(c => c.label === reason)
    || PC_CATEGORIES.find(c => reason.startsWith(`${c.label} :`))
    || null;
  const openMenu = (e: React.MouseEvent, id: string) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const MENU_H = 440;   // flip above the button when it would overflow the viewport bottom (8 categories + 2 fixed items)
    const y = (rect.bottom + 4 + MENU_H > window.innerHeight) ? Math.max(8, rect.top - MENU_H - 4) : rect.bottom + 4;
    setMenuFor({ id, x: Math.max(8, Math.min(rect.left, window.innerWidth - 290)), y });
  };
  // openPanel=true (the "w/ Receipt" categories): after tagging, open the match panel so the
  // operator can issue the receipt right away (confirm-match then overwrites the tag with the match).
  const doNoMatch = async (e: PettyCashEntry, reason: string, openPanel = false) => {
    setNoMatchBusy(e.id);
    try {
      await api.pettyNoMatch(e.id, reason);
      await load(page, true);
      if (openPanel) setSelectedMatch(e);
    }
    catch (er: any) { alert(er.message); }
    finally { setNoMatchBusy(null); }
  };
  const cancelNoMatch = async (e: PettyCashEntry) => {
    setNoMatchBusy(e.id);
    try { await api.pettyUnmatch(e.id); await load(page, true); }
    catch (er: any) { alert(er.message); }
    finally { setNoMatchBusy(null); }
  };

  // Per-row Match cell (inbound rows only).
  const matchCell = (e: PettyCashEntry): React.ReactNode => {
    if (!e.inbound) return null;
    const st = e.match_state || 'none';
    // VAT / WHT are collected to be remitted — no receipt, but they DO get the payment-received
    // notice (owner 2026-08-28).
    const taxNotice = (e as any).match?.docKind === 'vat' || (e as any).match?.docKind === 'wht' || (e as any).match?.docKind === 'pnd51';
    // Whether docNo is the receipt's own number rather than a quotation/invoice: since 2026-08-29 it is the parent's,
    // but it falls back to the receipt's when the match has no parent number (then docNo === receiptNo), and a row
    // matched before then has no receiptNo, its docNo being the receipt's. Tax-notice and Property Tax matches issue
    // no receipt: their docNo names the tax record and is kept.
    const md = (e as any).match;
    const docNoIsReceipt = md?.receiptNo ? md.docNo === md.receiptNo
      : !taxNotice && md?.docKind !== 'property_tax' && !['quotation', 'invoice'].includes(docKindOf(md?.docNo || ''));
    if (st === 'matched') return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }} onClick={ev => ev.stopPropagation()}>
        <span onClick={() => !taxNotice && e.receipt_id && openPdf(e.receipt_id)} style={{ color: '#15803D', fontWeight: 700, cursor: taxNotice ? 'default' : 'pointer' }} title={taxNotice ? t('bs_pt_no_receipt') : t('bs_open_receipt')}><i className={taxNotice ? 'ti ti-receipt-tax' : (pdfBusyId === e.receipt_id ? 'ti ti-loader-2' : 'ti ti-circle-check')} style={pdfBusyId === e.receipt_id && !taxNotice ? { display: 'inline-block', animation: 'spin 1s linear infinite' } : undefined} />
          {/* Receipts are deliberately not shown in this column (Calem, report 0b469f7f: "do not include receipts in
              the 'match quotation/invoice' column"): no receipt number beside the quotation/invoice, and none in its
              place, so a row whose docNo is the receipt's shows the tick alone. The tick still opens the receipt PDF;
              the quotation/invoice number stays a DocNoLink (Bugify #b4cccf02), a click opening its document. */}
          {!docNoIsReceipt && <>{' '}<DocNoLink no={md?.docNo} id={md?.docId} kind={md?.docKind} /></>}
        </span>
        <button onClick={() => taxNotice ? setTaxEmailFor(e.id) : emailMatched(e)} title={taxNotice ? t('tp_send_notice') : t('bs_send_email')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: PC_COLOR }}><i className="ti ti-send" style={{ fontSize: 13 }} /></button>
        <button onClick={() => undoMatch(e)} title={t('bs_unmatch')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-arrow-back-up" style={{ fontSize: 13 }} /></button>
        {/* Only a job already linked to this payment shows here, as its number. The column offers no Create Job any
            more (Calem, report 0b469f7f, 2nd opening: "Remove the 'Create Job' option from the 'Match Quotation/Invoice'
            column"), and with it went the bell that stopped its reminder and the "Job done elsewhere" chip that undid
            the bell. */}
        {(e as any).jobNo ? (
          <span title={(e as any).jobTitle || ''} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 7px', borderRadius: 8, background: '#EFF6FF', color: '#1D4ED8', fontSize: 11, fontWeight: 700 }}><i className="ti ti-briefcase" style={{ fontSize: 12 }} /> #{Number((e as any).jobNo).toLocaleString()}</span>
        ) : null}
      </span>
    );
    // Classification / no-match chip + cancel. Colour-coded by category: gray = internal funds,
    // blue = our income with a receipt still to issue, red = pass-through / custom reasons.
    if (st === 'ignored') {
      const reason = e.match?.reason || t('bs_no_match_generic');
      const cat = catForReason(reason);
      const tone = TONE[cat?.tone ?? 'red'];
      const icon = cat?.icon || 'ti ti-ban';
      const busy = noMatchBusy === e.id;
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%' }} onClick={ev => ev.stopPropagation()}>
          <span title={reason} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 8, background: tone.bg, border: `1px solid ${tone.border}`, color: tone.text, fontSize: 11.5, fontWeight: 600, overflow: 'hidden' }}>
            {/* Owner 2026-08-27: the category name wraps instead of truncating — this column is
                important information and must be fully readable. */}
            <i className={icon} style={{ fontSize: 12, flexShrink: 0 }} /><span style={{ whiteSpace: 'normal', lineHeight: 1.3 }}>{reason}</span>
          </span>
          <button onClick={() => cancelNoMatch(e)} disabled={busy} title={t('bs_cancel_nomatch')} style={{ border: 'none', background: 'none', cursor: busy ? 'default' : 'pointer', color: 'var(--text-tertiary)', padding: 2 }}><i className={busy ? 'ti ti-loader' : 'ti ti-x'} style={{ fontSize: 14 }} /></button>
        </span>
      );
    }
    // A reason already chosen in Comments explains the money, so no match is asked for (Calem, report 2cf37913,
    // 2nd opening: "Any cash in row with the comments reason chosen already. When a row qualifies the match column
    // simply shows nothing"). The badge, the 08:00 email and the tasks bell leave these rows out by the same rule
    // (pettyAwaitingMatch in server/src/services/badgeScope.ts). Matched and classified rows were drawn above.
    if ((e.comments || '').trim()) return null;
    // Un-matched (none / unmatched / suggested): the Match ▾ dropdown. Suggested rows also show the AI hint.
    const busy = noMatchBusy === e.id;
    const ddBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', cursor: busy ? 'default' : 'pointer', fontSize: 11.5, fontWeight: 600, flexShrink: 0 };
    if (st === 'suggested') return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%' }} onClick={ev => ev.stopPropagation()}>
        <button onClick={() => setSelectedMatch(e)} style={{ minWidth: 0, maxWidth: 150, textAlign: 'left', display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 8, border: '1px solid #BFDBFE', background: '#EFF6FF', color: '#1D4ED8', cursor: 'pointer', fontSize: 11.5, fontWeight: 600, overflow: 'hidden' }}>
          <i className="ti ti-bulb" style={{ fontSize: 13, flexShrink: 0 }} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayClientName(e.match?.clientName) || '?'}{e.match?.docNo ? ' · ' + e.match.docNo : ''}</span>
        </button>
        <button onClick={ev => openMenu(ev, e.id)} disabled={busy} title={t('bs_match_btn')} style={{ ...ddBtn, padding: '3px 5px' }}><i className={busy ? 'ti ti-loader' : 'ti ti-chevron-down'} style={{ fontSize: 13 }} /></button>
      </span>
    );
    return (
      <button onClick={ev => { ev.stopPropagation(); openMenu(ev, e.id); }} disabled={busy} style={ddBtn}>
        <i className={busy ? 'ti ti-loader' : 'ti ti-search'} style={{ fontSize: 12 }} /> {matching ? '…' : t('bs_match_btn')} <i className="ti ti-chevron-down" style={{ fontSize: 12 }} />
      </button>
    );
  };

  useEffect(() => { (async () => { await load(1); runAutomatch(); })(); }, [badgeOnly, pageSize]); // eslint-disable-line
  // Server-wide search (owner 2026-08-31): debounced refetch — the box used to filter only the
  // 100 rows already on screen, so anything past page 1 looked like it did not exist.
  // Skip the mount run — it double-loaded the list on open (owner 2026-09-06).
  const qInit = useRef(true);
  useEffect(() => { if (qInit.current) { qInit.current = false; return; } const t = setTimeout(() => { void load(1); }, 400); return () => clearTimeout(t); }, [q]); // eslint-disable-line
  // A bubble lit or let go reloads page 1 straight away; skips the mount run as the search does.
  const kindInit = useRef(true);
  useEffect(() => { if (kindInit.current) { kindInit.current = false; return; } void load(1); }, [kind]); // eslint-disable-line

  // Search covers text fields AND every amount (owner 2026-08-25: "还需要支持搜索数字") — amounts are
  // indexed both raw ("42150.25") and display-formatted ("42,150.25"), and commas are stripped from
  // both sides so "42,150" and "42150" find the same row.
  const filtered = entries.filter(e => {
    if (!q.trim()) return true;
    // The server already searched every entry — a second, slightly different client-side pass
    // would drop real hits. Rows ARE the result set.
    return true;
    const nums = [e.inbound, e.outbound, e.closing_balance].filter(v => Number(v)).flatMap(v => [String(v), money(v)]);
    const hay = [e.from_party, e.to_party, e.comments, fmtDate(e.txn_date), e.txn_date, e.updated_by_name, ...nums].join(' ').toLowerCase().replace(/,/g, '');
    return hay.includes(q.trim().toLowerCase().replace(/,/g, ''));
  });

  // The header on the page's white, --bg-surface, as the Jobs lists' header is (Calem, report ddf1ba0e, 3rd opening —
  // see cardLine), so the headings and the page are one colour parted by the headings' thin line. It was the light grey,
  // --c-gray-50 (Calem, report c0d8d666: "Revert to background light gray color for all menus that look similar to
  // this"), and the near-white before that. No line under the cells, which draw the cards' edges instead (see
  // cardLine). Calem, report e981f781, 3rd opening: "Make the gray text menu 1 pt larger … Also add the light horizontal
  // gray line below the menu" — the labels 11 → 12px, as the top bar's labels went a point larger (Portal, report
  // b783560e), and the thin grey line under every heading again, the Jobs and Peak lists' own, 1px of --border, carried
  // out to the list's edges (thEdge).
  // Then Active Jobs' headings, as every staff list's: black, --text-primary, at the regular weight, where they were the
  // light grey --text-tertiary at semibold (600), on a 16px line where it was 1.25 (15px), over the darker --border-strong
  // line (Calem, report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like this: the same height, text
  // color, no bold, horizontal line colors etc."). The 12px stays; the band is a pixel taller, 37px as every list's is.
  const th: React.CSSProperties = { textAlign: 'left', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 9px', lineHeight: '16px', borderBottom: '1px solid var(--border-strong)', position: 'sticky', top: 0, zIndex: 1, background: 'var(--bg-surface)' };
  // The header's white and its line carried on over the table's CARD_INSET padding to the list's edges, leftwards from
  // the Date header and rightwards from the last, as on the Bank Statement (thEdge there): the cards come in, the header
  // doesn't. Inside a header cell, so it stays on the header as the list scrolls under it; it reaches a pixel below the
  // cell's inside (bottom −1), as the Jobs and Peak lists' thEdge does, so its line meets the cell's — the darker
  // --border-strong now (report 4bd4ae4e — see th).
  const thEdge: React.CSSProperties = { position: 'absolute', top: 0, bottom: -1, width: CARD_INSET, background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-strong)' };
  const td: React.CSSProperties = { fontSize: 13, color: 'var(--text-primary)', padding: '9px 9px', borderTop: cardLine, borderBottom: cardLine, whiteSpace: 'nowrap' };
  const edgeL: React.CSSProperties = { borderLeft: cardLine, borderTopLeftRadius: CARD_R, borderBottomLeftRadius: CARD_R };
  const edgeR: React.CSSProperties = { borderRight: cardLine, borderTopRightRadius: CARD_R, borderBottomRightRadius: CARD_R };
  // The page above each card, and a strip more under the last one. See-through by a colour set on the row, not by having
  // none: global.css's zebra shades every other row of a list with a header, so a gap on an even row would come out darker.
  const cardGap = (key: string) => (
    <tr key={key} aria-hidden="true" style={{ background: 'transparent' }}><td colSpan={12} style={{ padding: 0, height: CARD_GAP, border: 'none' }} /></tr>
  );
  // Calem, report e981f781: 20% of Comments' width goes to From and To, half each, on any screen. Comments is the one
  // col with no width, so it has whatever the table has past its CARD_INSET padding either side and the other cols'
  // 1274px. The table is measured, From and To each get a tenth of that on top of their own width (see the colgroup), and
  // Comments keeps the other 80%. The table is only on the page once the list has loaded, so it is measured again each
  // time it comes back. The other cols come to 1195px now, 79 under 1274: 20px, a third of the 60 Match
  // Quotation/Invoice gave up in report 0b469f7f's 3rd opening (330 → 270), all 40 it gave up in report 45e62522
  // (270 → 230), and the 19 it and Recorded by gave up in report f7463eaa (230 → 221, 103 → 93). The tenths are still
  // of what is past 1274, so those 79px are Comments' alone (see the colgroup).
  // Calem, report 45e62522, 2nd opening: To is drawn 5% narrower, its tenth included (see the colgroup). give is
  // measured as before, and what To gives up is Comments' too.
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [give, setGive] = useState(0);
  useLayoutEffect(() => {
    const el = tableRef.current;
    if (!el) return;
    const measure = () => setGive(Math.round(Math.max(0, el.getBoundingClientRect().width - 2 * CARD_INSET - 1274) * 0.10));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  const openNew = () => { setEditing(null); setShowForm(true); };
  const openEdit = (e: PettyCashEntry) => { setEditing(e); setShowForm(true); };

  // Deep-link from the Petty Cash reminder emails: ?open=<id> opens that entry's match panel. The
  // entry may sit on a later page, so fall back to fetching it directly if it isn't in the list.
  const [params, setParams] = useSearchParams();
  const openedRef = useRef(false);
  useEffect(() => {
    const openId = params.get('open');
    if (!openId || openedRef.current) return;
    const found = entries.find(e => e.id === openId);
    if (found) { openedRef.current = true; openEdit(found); params.delete('open'); setParams(params, { replace: true }); return; }
    // Not on the current page — fetch it once. (Guarded so we don't loop if the id is stale.)
    if (!loading && !openedRef.current) {
      openedRef.current = true;
      api.getPettyCashOne(openId).then((e: any) => { if (e && e.id) openEdit(e); }).catch(() => {})
        .finally(() => { params.delete('open'); setParams(params, { replace: true }); });
    }
  }, [entries, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // The page — round the list as between and under the cards — is white, --bg-surface, the column headings' own (Calem,
  // report ddf1ba0e, 3rd opening: "Change every page background to white"). Round the list it was the app's beige,
  // until Bugify #b07c8dae's 1st opening made the lot the coral one shade lighter, #FBF1ED, and its 2nd the list's grey,
  // --c-gray-50.
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-surface)' }}>
      {/* Toolbar: Outbound / Inbound / Banking + search + refresh.
          The light grey, --c-gray-50, as File Storage's toolbar band is and every top-menu
          page's now (Calem, report 3e769579, 4th opening: "Only the top menu strip on every page turns that same grey
          then the rest of the page below this menu has a white background"). It was the page's white, --bg-surface; the
          page under it stays white. Its line is a shade darker, --border-strong where it was --border, as is the top
          menu's over it (Calem, report a903bda5, 3rd opening: "Make the gray horizontal lines one shade darker on all
          page menus like this"). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--border-strong)', background: 'var(--c-gray-50)' }}>
        {/* The Outbound / Inbound / Banking bubbles (Calem, report 19f4cbbe: "Add the following (narrow search) bubbles to
            the PettyCash menu"), at the toolbar's left as Tracking's status pills are: each narrows the list, the search
            included, to cash out, cash in or cash paid into the bank (the server's ?kind=). A click on the lit one lets it
            go and the whole list is back. The Bank Statement period pills' size and round ends, so they stand as tall as
            the search box, in menuPill's Petty Cash coral — the lit one solid, the rest the box's pale wash. */}
        {([['out', t('pc_outbound')], ['in', t('pc_inbound')], ['banked', t('pc_f_banking')]] as const).map(([k, label]) => (
          <button key={`kind-${k}`} onClick={() => setKind(v => (v === k ? '' : k))} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 999, border: '1px solid', ...menuPill('pettycash', kind === k), cursor: 'pointer' }}>{label}</button>
        ))}
        <div style={{ flex: 1 }} />
        {/* The search box is a round bubble in the top menu's Petty Cash coral (Calem, Bugify #cba01676, 6th opening:
            "Do the same (colored bubble search) for quotation/invoice/receipts, email/calendar and petty cash"), dressed
            as Bank Statement's search box: round ends, a 1px line, 5px 12px and 12px semibold, in the Petty Cash box's
            colours from menuPill — its pale coral wash, its line and coral words, the placeholder's too (mf-ph-inherit)
            — with the magnifier in the coral 12px in and the words 30px in, clear of it. Its place, its width and its
            filtering are as they were. It was an 8px-cornered box with the faint grey outline on the app's beige,
            --bg-app, its grey magnifier 10px in and its words 28px. Its wash is a shade darker than the box's,
            #FAECE7 → #F9E7E1, as the cards' went a shade darker (report 0fb815da, 3rd opening — see PC_ROW_BG); its
            line and words, and the box, keep their own.
            It is Filing's search box's size now and reads "Search petty cash…", where it read "Search…" (Calem, report
            48c2197c, 2nd opening: "Do the same for every search bar. Put the name inside"): 13.5px words where they
            were 12, 7px above and below them where it was 5, the magnifier 14.5px and 16px in, the words 36px in and
            16px clear of the right-hand end, 220px wide where it was 200 so they fit. It reaches 3px into the
            toolbar's padding above and below, so it takes the room it took and the toolbar stays as tall. */}
        <div style={{ position: 'relative', margin: '-3px 0' }}>
          <i className="ti ti-search" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 14.5, color: PC_COLOR }} aria-hidden />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('pc_search_petty')} className="mf-ph-inherit" style={{ width: 220, padding: '7px 16px 7px 36px', fontSize: 13.5, fontWeight: 600, borderRadius: 999, border: '1px solid', outline: 'none', ...menuPill('pettycash', false), background: lighten(TAB_GROUPS.petty.tint, -1) }} />
        </div>
        {matching && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: PC_COLOR, fontWeight: 600 }}><i className="ti ti-loader" style={{ fontSize: 14 }} aria-hidden />{t('bs_matching')}</span>}
        <button onClick={() => load()} title={t('refresh')} style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
          <i className="ti ti-refresh" style={{ fontSize: 15 }} aria-hidden />
        </button>
      </div>

      {/* Badge drill-down strip (?badge=1): only the unmatched incoming rows the red badge counts. */}
      {badgeOnly && <BadgeFilterBar count={total} labelKey="bf_bank" onClear={clearBadge} />}

      {/* Table — on the page's white, as on the Bank Statement: between the cards, down either side of them and on
          under the last one (see cardLine). */}
      <div style={{ flex: 1, overflow: 'auto', background: 'var(--bg-surface)' }}>
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '10px 16px' }}>{err}</div>}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} aria-hidden /></div>
        ) : (
          // Separate borders with no spacing, as on the Bank Statement: collapsed borders can't be rounded. CARD_INSET of
          // padding either side, where the page's white shows. Every box is border-box (global.css), so the minimum width
          // takes the padding on top of the columns' own 1474, and no column comes in for it.
          <table ref={tableRef} style={{ width: '100%', minWidth: 1474 + 2 * CARD_INSET, borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', padding: `0 ${CARD_INSET}px` }}>
            {/* Fixed layout (owner 2026-08-27): every short column gets a tight fixed width and
                Comments — the only col with no width — absorbs ALL remaining space, so long
                notes read as lines, not a squeezed vertical sliver.
                Calem, report 301a74b8: no Banked column — a tick in Comments instead. Its 54px went to
                Match / Receipt (162 → 216), and To took 22px (112 → 134), a tenth of the ~222px
                Comments had at the 1360 minimum.
                Calem, report a598e54f: 15% of Comments went to Match / Receipt — 114px of the ~760px
                it had on Calem's screen (216 → 330). The minimum rose by the same 114px (1360 → 1474)
                so Comments keeps its ~200px at the narrowest.
                Calem, report e981f781: 20% of Comments now goes to From and To, 10% each, at every screen
                width (give — measured above). The minimum stays 1474: there From and To gain 20px each and
                Comments keeps 160px. The cards' CARD_INSET either side (that report's 2nd opening) comes on
                top of it, off Comments' share before the 20% is taken.
                Calem, report d9d2eaaa, 2nd opening: Recorded by 3% narrower, and that width to Collection —
                3px, 3% of Recorded by's own width as report a598e54f's 15% was of Comments' own (106 → 103,
                132 → 135). The columns' 1274 and the 1474 minimum are as they were.
                Calem, report 0b469f7f, 3rd opening: 18% of Match Quotation/Invoice's width goes to To, From and
                Comments, a third each — 60px of its 330 (18% is 59.4), so 330 → 270, From 112 → 132, To
                134 → 154 and Comments 20px more. The columns' own widths come to 1254 now, but give is still a
                tenth of what is past 1274 (see above), so From and To's share of Comments is what it was and all
                20px stay Comments'. The minimum stays 1474: there Comments has 180px where it had 160.
                Calem, report 45e62522: 15% of Match Quotation/Invoice to Comments — 40px of its 270 (15% is
                40.5), 270 → 230. The columns' own widths come to 1214 now; give is still a tenth of what is past
                1274, so From and To are as they were and all 40px are Comments'. The minimum stays 1474: there
                Comments has 220px where it had 180. A matched row's job pill may drop to a second line now (the
                cell wraps — see matchCell).
                Calem, report 45e62522, 2nd opening: To 5% narrower, and that width to Comments — 5% of To's own
                width as it is drawn, 154 + give, as report d9d2eaaa's 3% was of Recorded by's own, so the cut grows
                with the screen (about 11px at 1920 wide). From, Match Quotation/Invoice, give and the 1474 minimum
                are as they were: there To has 165px where it had 174 and Comments 229 where it had 220. A long name
                in To may wrap a little sooner (the cell wraps).
                Calem, report f7463eaa: 10% of Recorded by's width and 4% of Match Quotation/Invoice's to Comments —
                10px of Recorded by's 103 (10% is 10.3), 103 → 93, and 9px of Match Quotation/Invoice's 230 (4% is
                9.2), 230 → 221. The columns' own widths come to 1195 now; give is still a tenth of what is past 1274,
                so From and To are as they were and all 19px are Comments'. The minimum stays 1474: there Comments
                has 248px where it had 229. A long name in Recorded by, and a matched row's job pill, may drop to a
                second line a little sooner (both cells wrap). */}
            <colgroup>
              <col style={{ width: 86 }} />{/* date */}
              <col style={{ width: 90 }} />{/* opening */}
              <col style={{ width: 84 }} />{/* inbound */}
              <col style={{ width: 132 + give }} />{/* from */}
              <col style={{ width: 90 }} />{/* closing */}
              <col style={{ width: 84 }} />{/* outbound */}
              <col style={{ width: Math.round((154 + give) * 0.95) }} />{/* to */}
              <col />{/* comments — the rest */}
              <col style={{ width: 221 }} />{/* match / receipt */}
              <col style={{ width: 135 }} />{/* collection */}
              <col style={{ width: 93 }} />{/* recorded */}
              <col style={{ width: 26 }} />{/* chevron */}
            </colgroup>
            {/* The first and last header cells let their thEdge show past them. Opening, Closing and Match
                Quotation/Invoice are the labels from Calem's screenshot (report e981f781, 3rd opening), each on a key
                of its own: the entry form still says Opening Balance and Closing Balance, and the Bank Statement
                Match / Receipt. */}
            <thead><tr>
              <th style={th}><span aria-hidden style={{ ...thEdge, left: -CARD_INSET }} />{t('pc_date')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('pc_opening_head')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('pc_inbound')}</th>
              <th style={th}>{t('pc_from')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('pc_closing_head')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('pc_outbound')}</th>
              <th style={th}>{t('pc_to')}</th>
              <th style={th}>{t('pc_comments')}</th>
              <th style={th}>{t('pc_match')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('bs_collection')}</th>
              <th style={th}>{t('pc_recorded')}</th>
              <th style={th}><span aria-hidden style={{ ...thEdge, right: -CARD_INSET }} /></th>
            </tr></thead>
            <tbody>
              {/* A gap row above each card (see cardGap). The fill is on the row, as on the Bank Statement's cards,
                  where the rounded end cells give it its corners: on the cells it would cover the red ring a
                  tasks-bell link flashes on the row. Hover takes that fill three shades darker, down lighten's own
                  scale as PC_BANKED_BG is (#FBF1ED → #F8E7E0), and gives it back on leaving (Calem, report
                  28e90b5f, 2nd opening: "Instead of two shades lighter make it three shades darker"). It took it
                  two shades lighter (the 1st opening), and turned the card white, --bg-active, before that. */}
              {filtered.flatMap(e => [
                cardGap(`gap-${e.id}`),
                <tr key={e.id} data-row-id={e.id} onClick={() => openEdit(e)} style={{ cursor: 'pointer', background: e.banked ? PC_BANKED_BG : PC_ROW_BG }}
                  onMouseEnter={ev => (ev.currentTarget.style.background = lighten(e.banked ? PC_BANKED_BG : PC_ROW_BG, -3))}
                  onMouseLeave={ev => (ev.currentTarget.style.background = e.banked ? PC_BANKED_BG : PC_ROW_BG)}>
                  <td style={{ ...td, ...edgeL }}>{fmtDate(e.txn_date)}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{e.opening_balance ? fmtAmt(e.opening_balance) : ''}</td>
                  <td style={{ ...td, textAlign: 'right', color: 'var(--accent)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{e.inbound ? fmtAmt(e.inbound) : ''}</td>
                  {/* From / To tidied as every list shows a client (Calem, report 101b5c69): titles and a company's
                      legal form off. The entry itself keeps what was typed. */}
                  <td style={{ ...td, whiteSpace: 'normal', color: e.inbound ? 'var(--accent)' : 'var(--text-primary)' }}>{displayClientName(e.from_party)}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{e.closing_balance ? fmtAmt(e.closing_balance) : ''}</td>
                  <td style={{ ...td, textAlign: 'right', color: e.banked ? PC_BANKED_BLUE : '#E24B4A', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{e.outbound ? fmtAmt(e.outbound) : ''}</td>
                  <td style={{ ...td, whiteSpace: 'normal', color: e.outbound ? (e.banked ? PC_BANKED_BLUE : '#E24B4A') : 'var(--text-primary)' }}>{displayClientName(e.to_party)}</td>
                  {/* A banked (Y) row opens its Comments with a tick, in place of the Banked column
                      (Calem, report 301a74b8). An N row shows nothing.
                      Calem, report d07e03d3: "Make this circle with the tick inside it larger, the same size as
                      other circles on, say, active jobs or closed job pages. , change it to match the blue banking
                      color." It was a bare 14px green circle-check; now a tick in a 20px circle built as Jobs' day
                      circle and comment plus are (see CommentPlus in Jobs), outline and tick in the row's banking
                      blue (PC_BANKED_BLUE, as its Outbound and To). The cell is a line of text, not a flex row,
                      hence the verticalAlign: it centres the circle on the first line. */}
                  <td style={{ ...td, whiteSpace: 'pre-line', color: 'var(--text-secondary)', lineHeight: 1.45, wordBreak: 'break-word' }}>
                    {e.banked ? (
                      <span title={t('pc_banked_tip')} aria-hidden style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxSizing: 'border-box', width: 20, height: 20, marginRight: 6, borderRadius: '50%', border: `1px solid ${PC_BANKED_BLUE}`, verticalAlign: 'middle' }}>
                        <i className="ti ti-check" style={{ fontSize: 12, color: PC_BANKED_BLUE }} />
                      </span>
                    ) : null}{listComments(e)}
                  </td>
                  <td style={{ ...td, whiteSpace: 'normal' }}>{matchCell(e)}</td>
                  {/* Collection (代收代付) — same shared cell as the Bank Statement, incl. the
                      per-tax Paid tick with its own date (owner 2026-08-24). Only the purple total
                      sits in the cell; the tax breakdown and Paid ticks pop down from the round toggle
                      beside it, as on the Bank Statement (Calem, report 33249b9a) — drawn in the cell,
                      they made the row several lines tall.
                      The total and its toggle sit in the middle of the row, as every other cell's words do
                      (Calem, report f7463eaa: "center the 'Collection' vertically"); they were pinned to its
                      top. The pop-down still opens under the amount, wherever that sits (CollectionCell's
                      place measures it). */}
                  <td style={{ ...td, textAlign: 'right', verticalAlign: 'middle' }} onClick={ev => ev.stopPropagation()}>
                    <CollectionCell row={e as any} onSetPaid={(taxKey, paidOn) => setTaxPaid(e, taxKey, paidOn)} popDown />
                  </td>
                  <td style={{ ...td, color: 'var(--text-tertiary)', whiteSpace: 'normal' }}>
                    <div style={{ fontWeight: 500, color: 'var(--text-secondary)', fontSize: 12 }}>{e.updated_by_name || '—'}</div>
                    <div style={{ fontSize: 11 }}>{fmtDateTime(e.updated_at)}</div>
                  </td>
                  <td style={{ ...td, ...edgeR, color: 'var(--text-tertiary)' }}><i className="ti ti-chevron-right" aria-hidden /></td>
                </tr>,
              ])}
              {filtered.length > 0 && cardGap('end')}
              {/* No entries: the words on the page's white, not in a card. */}
              {filtered.length === 0 && (
                <tr><td style={{ ...td, borderTop: 'none', borderBottom: 'none', textAlign: 'center', color: 'var(--text-tertiary)', padding: 40 }} colSpan={12}>{t('pc_empty')}</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} total={total} limit={pageSize} onPage={p => load(p)} onLimit={n => setPageSize(n)} />

      {/* Floating add — the shared "+", in the top menu's Petty Cash colour (Bugify #7568b3b5) */}
      <Fab onClick={openNew} title={t('pc_add')} color={tabColor('pettycash')} />

      {showForm && <EntryForm entry={editing} lastClosing={lastClosing} onClose={() => setShowForm(false)} onToRenamed={() => load(page, true)} onSaved={link => {
        load(page, true);
        if (!link) { runAutomatch(); return; }
        // A document picked in the details: open the Match panel on it for this entry — if money came in,
        // as the Match column asks. The auto-match waits for the panel to close: its AI answer rewrites
        // the entry's match state when it lands, and would land on a match confirmed in the meantime.
        api.getPettyCashOne(link.entryId)
          .then((e: any) => { if (e?.id && Number(e.inbound) > 0) { setMatchKind(undefined); setFormPick(link.doc); setSelectedMatch(e); } else runAutomatch(); })
          .catch(() => runAutomatch());
      }} />}
      {selectedMatch && <MatchPanel
        payment={{ amount: selectedMatch.inbound, dateLabel: fmtDate(selectedMatch.txn_date), remitter: displayClientName(selectedMatch.from_party) || '—', note: selectedMatch.comments || '' }}
        suggestion={formPick ? { ...formPick, reason: t('pc_doc_reason') } : selectedMatch.match || null}
        loadCandidates={(qq, amt, opts) => api.pettyMatchCandidates(qq, amt, opts).then((d: any) => d.candidates || [])}
        loadDocItems={(kind, id) => api.pettyDocItems(kind, id)}
        confirmMatch={(body) => api.pettyConfirmMatch(selectedMatch.id, body)}
        ignoreMatch={() => api.pettyNoMatch(selectedMatch.id, t('pc_nm_generic'))}
        kindFilter={matchKind} txnSource="petty" txnId={selectedMatch.id}
        onClose={() => { setSelectedMatch(null); setMatchKind(undefined); if (formPick) { setFormPick(null); runAutomatch(); } }} onDone={() => load(page, true)} />}
      {taxEmailFor && <TaxPaidEmailComposer source="petty" txnId={taxEmailFor} onClose={() => setTaxEmailFor(null)} onSent={() => load(page, true)} />}
      {rcEmailFor && <ReceiptEmailComposer receiptId={rcEmailFor} onClose={() => setRcEmailFor(null)} onSent={() => load(page, true)} />}
      {undoJob && <UndoWithJobModal job={undoJob.job} busy={undoBusy}
        onCancel={() => setUndoJob(null)} onConfirm={confirmUndoWithJob} />}

      {/* Match / No-match dropdown (anchored to the Match ▾ button). */}
      {menuFor && (() => {
        const r = entries.find(x => x.id === menuFor.id) || null;
        if (!r) return null;
        const item = (icon: string, label: string, onClick: () => void, color?: string) => (
          <button key={label} onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left', padding: '8px 13px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: color || 'var(--text-primary)' }}
            onMouseEnter={ev => (ev.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}>
            <i className={icon} style={{ fontSize: 15, width: 16, textAlign: 'center' }} />{label}
          </button>
        );
        return (
          <>
            <div onClick={() => setMenuFor(null)} style={{ position: 'fixed', inset: 0, zIndex: 11000 }} />
            <div style={{ position: 'fixed', left: menuFor.x, top: menuFor.y, zIndex: 11001, minWidth: 272, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-md)', overflow: 'hidden', padding: '4px 0' }}>
              {/* Real document matching — behavior unchanged. */}
              {item('ti ti-plug-connected', t('pc_nm_match'), () => { setMenuFor(null); setMatchKind(undefined); setSelectedMatch(r); })}
              {item('ti ti-receipt-tax', t('bs_match_vat'), () => { setMenuFor(null); setMatchKind('vat'); setSelectedMatch(r); })}
              {item('ti ti-scissors', t('bs_match_wht'), () => { setMenuFor(null); setMatchKind('wht'); setSelectedMatch(r); })}
              {item('ti ti-calendar-stats', t('bs_match_pnd51'), () => { setMenuFor(null); setMatchKind('pnd51'); setSelectedMatch(r); })}
              <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
              <div style={{ padding: '3px 13px 4px', fontSize: 10, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>Classify as</div>
              {PC_CATEGORIES.map(c => item(c.icon, c.label, () => {
                setMenuFor(null);
                if (c.ask === 'notary') { setNotaryFirst(''); setNotaryLast(''); setNotaryFor(r); return; }
                doNoMatch(r, c.label, !!c.panel);
              }, TONE[c.tone].text))}
              <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
              {item('ti ti-pencil', t('bs_nm_custom'), () => { setMenuFor(null); setCustomReason(''); setCustomFor(r); }, TONE.red.text)}
            </div>
          </>
        );
      })()}

      {/* Custom no-match reason → written to Comments. */}
      {notaryFor && (() => {
        const full = `${notaryLast.trim()}${notaryLast.trim() && notaryFirst.trim() ? ', ' : ''}${notaryFirst.trim()}`.trim();
        const ok = !!notaryLast.trim();
        const LABEL = 'Notary Public w/o Receipt';
        const save = () => { const rr = notaryFor; setNotaryFor(null); doNoMatch(rr, `${LABEL} : ${full}`); };
        const fld: React.CSSProperties = { width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };
        const lb: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.04em' };
        return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 11002, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={ev => ev.target === ev.currentTarget && setNotaryFor(null)}>
          <div style={{ width: 420, maxWidth: '94vw', background: 'var(--bg-surface)', borderRadius: 14, boxShadow: 'var(--shadow-md)', padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}><i className="ti ti-stamp" style={{ marginRight: 6 }} />{t('bs_notary_title')}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('bs_notary_hint')}</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={lb}>{t('bs_notary_first')}</label>
                <input value={notaryFirst} autoFocus onChange={ev => setNotaryFirst(ev.target.value)}
                  onKeyDown={ev => { if (ev.key === 'Enter' && ok) save(); }} style={fld} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lb}>{t('bs_notary_last')} *</label>
                <input value={notaryLast} onChange={ev => setNotaryLast(ev.target.value)}
                  onKeyDown={ev => { if (ev.key === 'Enter' && ok) save(); }} style={fld} />
              </div>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
              {t('bs_notary_preview')}: <b style={{ color: 'var(--text-primary)' }}>{LABEL}{full ? ` : ${full}` : ''}</b>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setNotaryFor(null)} style={{ padding: '8px 16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{t('cancel')}</button>
              <button onClick={save} disabled={!ok} style={{ padding: '8px 16px', borderRadius: 'var(--radius-md)', border: 'none', background: PC_COLOR, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: ok ? 1 : 0.6 }}>{t('save')}</button>
            </div>
          </div>
        </div>
        );
      })()}

      {customFor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 11002, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={ev => ev.target === ev.currentTarget && setCustomFor(null)}>
          <div style={{ width: 400, maxWidth: '94vw', background: 'var(--bg-surface)', borderRadius: 14, boxShadow: 'var(--shadow-md)', padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{t('bs_nm_custom_title')}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('pc_nm_custom_hint')}</div>
            <textarea value={customReason} onChange={ev => setCustomReason(ev.target.value)} rows={3} placeholder={t('bs_nm_custom_ph')} autoFocus
              style={{ width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setCustomFor(null)} style={{ padding: '8px 16px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{t('cancel')}</button>
              <button onClick={() => { const rr = customFor; const txt = customReason.trim(); if (!txt) return; setCustomFor(null); doNoMatch(rr, txt); }} disabled={!customReason.trim()} style={{ padding: '8px 16px', borderRadius: 'var(--radius-md)', border: 'none', background: PC_COLOR, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: customReason.trim() ? 1 : 0.6 }}>{t('save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Standalone module page (/m/pettycash): brand header + the shared view ────
export default function PettyCash() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => navigate('/')} title={brandOr('Home')} style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t('mod_pettycash')}</div>
      </header>
      <PettyCashView />
    </div>
  );
}
